<script setup lang="ts">
import { computed, onMounted, type Ref, ref } from 'vue';
import cloneDeep from 'lodash.clonedeep';
import { AxiosError } from 'axios';
import {
  RBtn,
  useLoadingSpinnerStore,
} from '@therobotcompany/robco-frontend-library';
import gettext from '@/i18n/gettext';
import {
  type Customer,
  EMPTY_NEW_CUSTOMER,
  type NewCustomer,
} from '@/models/Customer';
import {
  createCustomer,
  loadCustomers,
  patchCustomer,
  deleteCustomer,
} from '@/api/AdminCustomerApi';
import CustomerCells from '@/components/admin/CustomerCells.vue';
import { requiredRule, numericOnlyRule } from '@/helpers/FormRules';
import useAuthStore from '@/store/AuthStore';
import { Permission } from '@/models/Roles';
import { useAlertsStore } from '@/store/AlertsStore';

const { $gettext } = gettext;
const loadingSpinnerStore = useLoadingSpinnerStore();
const authStore = useAuthStore();
const alerts = useAlertsStore();

const customers: Ref<Customer[]> = ref([]);
const editedCustomer: Ref<Customer | null> = ref(null);
const customerToDelete: Ref<Customer | null> = ref(null);
const editDialog = ref(false);
const createDialog = ref(false);
const deleteDialog = ref(false);
const searchQuery = ref('');
const newCustomer: Ref<NewCustomer> = ref(cloneDeep(EMPTY_NEW_CUSTOMER));

const createFormValid = ref(false);
const editFormValid = ref(true);
const createHubspotIdError = ref('');
const editHubspotIdError = ref('');

function hubspotIdErrorFromAxios(err: unknown): string | null {
  if (!(err instanceof AxiosError)) {
    return null;
  }
  const detail = (err.response?.data as { detail?: string } | undefined)
    ?.detail;
  const isHubspotIssue =
    typeof detail === 'string' && detail.toLowerCase().includes('hubspot');
  if (err.response?.status === 404 && isHubspotIssue) {
    return $gettext('This company ID was not found in HubSpot');
  }
  if (err.response?.status === 502) {
    return $gettext(
      'Could not reach HubSpot to validate company ID. Please try again later or remove the company ID',
    );
  }
  return null;
}

async function getCustomers() {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    customers.value = await loadCustomers();
  } catch (err) {
    alerts.error($gettext('Failed to load customers'));
    console.error(err);
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
}

async function submitCreateCustomer(customer: NewCustomer) {
  createHubspotIdError.value = '';
  try {
    await createCustomer(customer);
    alerts.success($gettext('Customer created'));
    getCustomers();
    closeCreateDialog();
  } catch (err) {
    const hubspotErr = hubspotIdErrorFromAxios(err);
    if (hubspotErr) {
      createHubspotIdError.value = hubspotErr;
    } else if (err instanceof AxiosError && err.response?.status === 409) {
      alerts.error(
        $gettext('Another customer with the same details already exists'),
      );
    } else {
      alerts.error($gettext('Failed to create customer'));
    }
    console.error(err);
  }
}

function closeEditDialog() {
  editedCustomer.value = null;
  editDialog.value = false;
}

async function saveChanges() {
  if (editedCustomer.value) {
    editHubspotIdError.value = '';
    try {
      await patchCustomer(editedCustomer.value);
      alerts.success($gettext('Customer updated'));
      getCustomers();
      closeEditDialog();
    } catch (err) {
      const hubspotErr = hubspotIdErrorFromAxios(err);
      if (hubspotErr) {
        editHubspotIdError.value = hubspotErr;
      } else if (err instanceof AxiosError && err.response?.status === 409) {
        alerts.error(
          $gettext('Another customer with the same details already exists'),
        );
      } else if (err instanceof AxiosError && err.response?.status === 404) {
        alerts.error($gettext('Customer not found'));
      } else {
        alerts.error($gettext('Failed to update customer'));
      }
      console.error(err);
    }
  }
}

function openCreateDialog() {
  newCustomer.value = cloneDeep(EMPTY_NEW_CUSTOMER);
  createHubspotIdError.value = '';
  createDialog.value = true;
}

function enableEdit(customer: Customer) {
  editedCustomer.value = cloneDeep(customer);
  editHubspotIdError.value = '';
  editDialog.value = true;
}

function closeCreateDialog() {
  createDialog.value = false;
}

function enableDelete(customer: Customer) {
  customerToDelete.value = customer;
  deleteDialog.value = true;
}

function closeDeleteDialog() {
  deleteDialog.value = false;
  customerToDelete.value = null;
}

async function confirmDelete() {
  if (customerToDelete.value) {
    try {
      await deleteCustomer(customerToDelete.value);
      alerts.success($gettext('Customer deleted'));
      getCustomers();
      closeDeleteDialog();
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 409) {
        alerts.error(
          $gettext(
            'Cannot delete customer: cells are still assigned. Delete the cells first.',
          ),
        );
      } else {
        alerts.error($gettext('Failed to delete customer'));
      }
      console.error(err);
    }
  }
}

const filteredCustomers = computed(() => {
  const query = (searchQuery.value || '').toLowerCase();
  return customers.value.filter((customer) => {
    const nameMatch = (customer.name || '').toLowerCase().includes(query);
    const notesMatch = (customer.internalNotes || '')
      .toLowerCase()
      .includes(query);
    const hubspotIdMatch = (customer.hubspotId || '')
      .toLowerCase()
      .includes(query);
    return nameMatch || notesMatch || hubspotIdMatch;
  });
});

const headers = computed(() => {
  const baseHeaders: object[] = [
    { title: $gettext('Name'), value: 'name', sortable: true },
    {
      title: $gettext('HubSpot Company ID'),
      value: 'hubspotId',
      sortable: true,
    },
    {
      title: $gettext('Internal Notes'),
      value: 'internalNotes',
      sortable: false,
    },
  ];

  if (authStore.hasPermission(Permission.PATCH_CUSTOMER)) {
    baseHeaders.push({
      title: $gettext('Edit'),
      key: 'edit',
      sortable: false,
      width: '100px',
    });
  }

  if (authStore.hasPermission(Permission.DELETE_CUSTOMER)) {
    baseHeaders.push({
      title: $gettext('Delete'),
      key: 'delete',
      sortable: false,
      width: '100px',
    });
  }

  return baseHeaders;
});

onMounted(() => {
  getCustomers();
});
</script>
<template>
  <div class="pa-4 pa-sm-12">
    <v-row>
      <v-col cols="12">
        <h1>
          {{ $gettext('Customers') }}
          <v-btn
            size="small"
            class="mb-1 ml-4 gradient-oceanblue-whitesands"
            icon="mdi-cached"
            @click="getCustomers"
          />
        </h1>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="mb-4">
        <v-row
          v-if="authStore.hasPermission(Permission.POST_CUSTOMER)"
          class="ma-4 ml-0"
        >
          <r-btn variant="outlined" @click="openCreateDialog">{{
            $gettext('Add Customer')
          }}</r-btn>
        </v-row>
        <v-row class="mt-12">
          <v-col cols="12" md="8" lg="6" xl="4">
            <v-text-field
              v-model="searchQuery"
              label="Search"
              outlined
              dense
              clearable
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-data-table
              items-per-page="25"
              :items="filteredCustomers"
              :headers="headers"
              show-expand
              item-value="uuid"
            >
              <template #item.name="{ item }">
                <div class="d-flex align-center">
                  <span>{{ item.name }}</span>
                  <v-btn
                    v-if="item.hubspotLink"
                    :href="item.hubspotLink"
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    variant="text"
                    class="ml-2"
                    :title="$gettext('View in HubSpot')"
                    @click.stop
                  >
                    {{ $gettext('View in HubSpot') }}
                    <v-icon end size="x-small" icon="mdi-open-in-new" />
                  </v-btn>
                </div>
              </template>
              <template #item.edit="{ item }">
                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props }">
                    <span v-bind="props">
                      <v-icon
                        icon="mdi-pencil"
                        @click.stop="enableEdit(item)"
                      ></v-icon>
                    </span>
                  </template>
                  {{ $gettext('Edit') }}
                </v-tooltip>
              </template>
              <template #item.delete="{ item }">
                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props }">
                    <span v-bind="props">
                      <v-icon
                        icon="mdi-trash-can-outline"
                        @click.stop="enableDelete(item)"
                      ></v-icon>
                    </span>
                  </template>
                  {{ $gettext('Delete') }}
                </v-tooltip>
              </template>
              <template
                #item.data-table-expand="{
                  internalItem,
                  toggleExpand,
                  isExpanded,
                }"
              >
                <v-btn
                  size="small"
                  variant="text"
                  @click.stop="toggleExpand(internalItem)"
                >
                  {{
                    isExpanded(internalItem)
                      ? $gettext('Hide Cells')
                      : $gettext('See Cells')
                  }}
                  <v-icon
                    end
                    size="small"
                    :icon="
                      isExpanded(internalItem)
                        ? 'mdi-chevron-up'
                        : 'mdi-chevron-down'
                    "
                  />
                </v-btn>
              </template>
              <template #expanded-row="{ columns, item }">
                <tr>
                  <td :colspan="columns.length">
                    <customer-cells
                      :customer-uuid="item.uuid"
                      :customer-name="item.name"
                    />
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-dialog v-model="createDialog" max-width="600">
      <v-card>
        <v-card-title>{{ $gettext('Add Customer') }}</v-card-title>
        <v-card-text>
          <v-form v-model="createFormValid" @submit.prevent>
            <v-text-field
              v-model="newCustomer.name"
              :label="$gettext('Name')"
              :rules="[requiredRule]"
            ></v-text-field>
            <v-text-field
              v-model="newCustomer.hubspotId"
              :label="$gettext('HubSpot Company ID')"
              :rules="[numericOnlyRule]"
              :placeholder="$gettext('Optional HubSpot Company ID')"
              :error-messages="createHubspotIdError"
              @update:modelValue="createHubspotIdError = ''"
            ></v-text-field>
            <v-textarea
              v-model="newCustomer.internalNotes"
              :label="$gettext('Internal Notes')"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="closeCreateDialog()">{{ $gettext('Close') }}</v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            :disabled="!createFormValid"
            @click="submitCreateCustomer(newCustomer)"
            >{{ $gettext('Add') }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-if="editedCustomer"
      v-model="editDialog"
      max-width="600"
      @click:outside="closeEditDialog"
    >
      <v-card>
        <v-card-title>{{ $gettext('Customer') }}</v-card-title>
        <v-card-text>
          <v-form v-model="editFormValid" @submit.prevent>
            <v-text-field
              v-model="editedCustomer.name"
              :label="$gettext('Name')"
              :rules="[requiredRule]"
            ></v-text-field>
            <v-text-field
              v-model="editedCustomer.hubspotId"
              :label="$gettext('HubSpot Company ID')"
              :rules="[numericOnlyRule]"
              :placeholder="$gettext('Optional HubSpot Company ID')"
              :error-messages="editHubspotIdError"
              @update:modelValue="editHubspotIdError = ''"
            ></v-text-field>
            <v-textarea
              v-model="editedCustomer.internalNotes"
              :label="$gettext('Internal Notes')"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="closeEditDialog">{{ $gettext('Close') }}</v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            :disabled="!editFormValid"
            @click="saveChanges"
            >{{ $gettext('Save') }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="deleteDialog" max-width="600px">
      <v-card>
        <v-card-title>
          {{ $gettext('Confirm Deletion') }}
        </v-card-title>
        <v-card-text>
          <p>
            {{ $gettext('Are you sure you want to delete this customer?') }}
          </p>
          <p>
            <strong>{{ customerToDelete?.name }}</strong>
          </p>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" variant="text" @click="closeDeleteDialog">{{
            $gettext('Cancel')
          }}</v-btn>
          <v-spacer />
          <v-btn color="error" variant="text" @click="confirmDelete">{{
            $gettext('Delete')
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
